<script setup lang="ts">
import { configDeliveryZones } from '@/storage/setting/configDeliveryZones'
import { ref, computed, onUnmounted } from 'vue'

const props = defineProps<{
    options: any[]
    // modelValue?: string | number | null
    displayKey?: string
    valueKey?: string | string[]
    placeholder?: string
    disabled?: boolean
    error?: boolean
    theme?: 'happy' | 'default' | 'admin'
    selected: any
}>()

const emit = defineEmits(['update:modelValue', 'change', 'sendError'])
const open = ref(false)
const isPlaceholder = ref(true)
const selectDisabledValue = ref(false)

const selectedValue = computed(() => {
    if (props.selected && props.valueKey === 'zone') {
        isPlaceholder.value = false
        const currentValue = configDeliveryZones.value.find(
            (item) => item.zone_id === props.selected.zone_id
        )

        return currentValue
            ? getItemDisplay(currentValue)
            : props.placeholder || 'Sélectionner...'
    }

    if (props.selected && props.valueKey !== 'zone') {
        isPlaceholder.value = false
        const currentValue = getItemDisplay(props.selected)
        return currentValue ? currentValue : props.placeholder || 'Sélectionner...'
    }

    isPlaceholder.value = false

    return props.placeholder || 'Sélectionner...'
})

const placeholderText = computed(() => {
    return props.placeholder || 'Sélectionner...'
})

const selectClasses = computed(() => {
    const theme = props.theme || 'default'
    const classes = ['custom-select', `custom-select--${theme}`]

    if (open.value && !props.disabled) {
        classes.push(`custom-select--open-${theme}`)
    }
    if (props.disabled) {
        classes.push(`custom-select--disabled-${theme}`)
    }
    if (props.error || selectDisabledValue.value) {
        classes.push(`custom-select--error-${theme}`)
    }
    return classes
})

const errorStyle = computed(() => {
    let error: boolean = false

    if (props.error) {
        error = true
    }

    if (selectDisabledValue.value) {
        error = true
    }

    return error
})

function getItemDisplay(item: any): string {
    if (Array.isArray(props.displayKey)) {
        return props.displayKey.map((key) => item[key]).join(' - ')
    }
    if (typeof props.displayKey === 'object') return item
    if (props.displayKey) return item[props.displayKey]
    if (typeof item === 'string' || typeof item === 'number') return String(item)
    return item.label || item.name || item.zone || String(item)
}

function getItemValue(item: any): string | number {
    if (Array.isArray(props.valueKey)) {
        return props.valueKey.map((key) => item[key]).join('-')
    }

    if (typeof props.valueKey === 'object') return item
    if (props.valueKey && item[props.valueKey]) return item[props.valueKey]

    if (typeof item === 'string' || typeof item === 'number') return item
    return item.value || item.id || item.zone || item
}

function toggle() {
    if (props.disabled) {
        emit('sendError', true)
        return
    }
    open.value = !open.value
}

function select(item: any) {
    const value = getItemValue(item)
    emit('update:modelValue', value)
    emit('change', item)
    open.value = false
}

function selectWrong() {
    selectDisabledValue.value = true
    setTimeout(() => {
        selectDisabledValue.value = false
    }, 2000)
}

function onClickOutside(event: MouseEvent) {
    const target = event.target as Element
    if (target && !target.closest('.custom-select')) {
        open.value = false
    }
}

window.addEventListener('click', onClickOutside)

onUnmounted(() => {
    window.removeEventListener('click', onClickOutside)
})
</script>

<template>
    <div :class="[selectClasses, { shake: errorStyle }]" @click="toggle">
        <div class="selected">
            <span
                class="selected-text"
                :class="{
                    'selected-text--placeholder': isPlaceholder
                }"
                >{{ selectedValue }}</span
            >
            <span class="arrow" :class="{ 'arrow--rotated': open }">▼</span>
        </div>

        <ul
            v-if="open && !disabled"
            class="options"
            :class="{ 'option--error': errorStyle }"
        >
            <li
                class="option"
                @click.stop="select('')"
                :class="{
                    'border-b border-border-gray-happy-light ': options.length > 0
                }"
            >
                {{ placeholderText }}
            </li>
            <li
                v-for="(item, index) in options"
                :key="index"
                class="option"
                :class="{
                    'option--selected':
                        getItemValue(item) === getItemValue(props.selected),
                    'option--disabled': item.disabled === true,
                    'border-b border-border-gray-happy-light ': index < options.length - 1
                }"
                @click.stop="item.disabled ? selectWrong() : select(item)"
            >
                <span class="" :class="{ 'line-through': item.disabled }">
                    {{ getItemDisplay(item) }}</span
                >
                <span
                    v-if="item.disabled"
                    class="no-underline text-border-red-happy-light font-semibold"
                >
                    {{ 'Commande minimum : ' + item.minimum_price + '€' }}
                </span>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.custom-select {
    @apply relative w-full cursor-pointer;
}

.custom-select--disabled-default {
    @apply opacity-50 cursor-not-allowed;
}

.custom-select--error-default .selected {
    @apply border-red-500 flex items-center;
}

.selected {
    @apply flex items-center flex-wrap justify-between px-2 py-1 min-h-8 border-2 border-banner-gray-component-happy-light rounded-md bg-bg-happy-light transition-colors;
}

.custom-select--open-happy .options {
    @apply absolute z-30;
}

.custom-select--open-default .selected {
    @apply border-blue-500;
}

.selected-text {
    @apply flex items-center justify-center text-center flex-1  text-gray-700;
}

.selected-text--placeholder {
    @apply text-gray-400;
}

.arrow {
    @apply transition-transform duration-200;
}

.arrow--rotated {
    @apply rotate-180;
}

.options {
    @apply absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-300 rounded-md shadow-lg  max-h-60 overflow-y-auto;
}
.options.option--error {
    @apply border-red-500;
}

.option {
    @apply px-2 py-1 min-h-8 flex flex-col items-center text-center justify-center hover:bg-gray-100 cursor-pointer transition-colors;
}

.option--selected {
    @apply bg-blue-100 text-blue-800 flex;
}

.option--disabled {
    @apply bg-banner-gray-component-happy-light;
}

.error-message {
    @apply text-red-500 text-sm mt-1;
}

/** Theme Happy */

.custom-select--happy {
    @apply happy-dark:bg-text-dark-happy-light happy-light:bg-bg-happy-light text-text-dark-happy-light;
}
.custom-select--open-happy .selected {
    @apply border-button-yellow-happy-light;
}
.custom-select--disabled-happy {
    @apply text-opacity-50 cursor-not-allowed;
}
.custom-select--error-happy .selected {
    @apply border-border-red-happy-light;
}

/*   'custom-select',
    `custom-select--${props.theme || 'default'}`,
    {
        [`custom-select--open-${props.theme || 'default'}`]: open.value,
        [`custom-select--disabled-${props.theme || 'default'}`]: props.disabled,
        [`custom-select--error-${props.theme || 'default'}`]: props.error
    } */

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(1deg);
    }
    30% {
        transform: rotate(0deg);
    }
    45% {
        transform: rotate(-1deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-1deg);
    }
    100% {
        transform: rotate(0deg);
    }
}
.shake {
    animation: shake 0.5s ease-in-out;
    @apply relative z-30;
}
</style>
